import { AbpModule, TokenService } from 'abp-ng2-module';
import { PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { AppConsts } from '@shared/AppConsts';
import { CommonModule } from '@shared/common/common.module';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { AppUiCustomizationService } from '@shared/common/ui/app-ui-customization.service';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { API_BASE_URL } from '@shared/service-proxies/service-proxies';
import { ServiceProxyModule } from '@shared/service-proxies/service-proxy.module';
import { AppPreBootstrap } from './AppPreBootstrap';
import { AppModule } from './app/app.module';
import { RootRoutingModule } from './root-routing.module';
import { RootComponent } from './root.component';
import { API_BASE_URL2 } from '../src/app/main/projects/settings.component';
import { AnonymousUserPageComponent, API_STRIPE_KEY_Public } from './anonymouspage/anonymoususerpage.component';
import { AnonymousUserCheckOutSuccessComponent } from './anonymouspage/anonymoususer.checkout.success.component';
import { AnonymousUserCheckOutCancelComponent } from './anonymouspage/anonymoususer.checkout.cancel.component';
import { AuthInterceptor, CustomHttpInterceptor } from './shared/service-proxies/AuthInterceptor';
import { API_STRIPE_KEY_SubscribePlans, SubscriptionPlanComponent } from '@app/main/subscriptions/subscriptionplans.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { RegisterComponent } from 'account/register/register.component';
import { LoginService } from 'account/login/login.service';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaLoaderService, ReCaptchaV3Service } from 'ng-recaptcha';
import { UtilsModule } from '@shared/utils/utils.module';
import { LoginComponent } from 'account/login/login.component';
import { NgOtpInputModule } from 'ng-otp-input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { environment } from 'environments/environment';
import { LoaderComponent } from '@app/main/cablepro/loader.component';
import { CustomTokenService } from 'services/custom-token.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TitleStrategy } from '@node_modules/@angular/router';
import { FixedStandaloneTitleStrategy } from '@shared/service-proxies/EventEmitterService';
import { RollbarErrorHandler, rollbarFactory, RollbarService } from 'rollbar-config';

export function appInitializerFactory(
    injector: Injector,
    platformLocation: PlatformLocation) {
    return () => {
        abp.ui.setBusy();
        const cookieName = environment.production ? 'Abp.AuthToken' : 'Abp.DevAuthToken';
        abp.auth.tokenCookieName = cookieName;
        handleLogoutRequest(injector.get(AppAuthService));

        return new Promise<boolean>((resolve, reject) => {
            AppConsts.appBaseHref = getBaseHref(platformLocation);
            let appBaseUrl = getDocumentOrigin() + AppConsts.appBaseHref;

            AppPreBootstrap.run(appBaseUrl, () => {
                if (UrlHelper.isMaintenanceUrl(location.href)) {
                    abp.ui.clearBusy();
                    resolve(true);
                    return;
                }

                // Get current hostname
                const hostname = location.host.toLowerCase();
                let isFromWebfacing = hostname === environment.WebFacingHost.toLowerCase();

                let appSessionService: AppSessionService = injector.get(AppSessionService);
                appSessionService.init(isFromWebfacing).then(
                    (result) => {

                        //Css classes based on the layout
                        let appUiCustomizationService: AppUiCustomizationService = injector.get(AppUiCustomizationService);
                        if (abp.session.userId) {
                            $('body').attr('class', appUiCustomizationService.getAppModuleBodyClass());
                        } else {
                            $('body').attr('class', appUiCustomizationService.getAccountModuleBodyClass());
                        }

                        abp.ui.clearBusy();
                        resolve(result);
                    },
                    (err) => {
                        abp.ui.clearBusy();
                        reject(err);
                    }
                );
            }, resolve, reject);
        });
    };
}

function getDocumentOrigin() {
    if (!document.location.origin) {
        return document.location.protocol + "//" + document.location.hostname + (document.location.port ? ':' + document.location.port : '');
    }

    return document.location.origin;
}

export function shouldLoadLocale(): boolean {
    return abp.localization.currentLanguage.name && abp.localization.currentLanguage.name !== 'en-US';
}

export function convertAbpLocaleToAngularLocale(locale: string): string {
    if (!AppConsts.localeMappings) {
        return locale;
    }

    const localeMapings = AppConsts.localeMappings.filter(
        m => m.from === locale
    );

    if (localeMapings && localeMapings.length) {
        return localeMapings[0]['to'];
    }

    return locale;
}

export function getRemoteServiceBaseUrl(): string {
    return AppConsts.remoteServiceBaseUrl;
}



export function getRemoteServiceCalBaseUrlSetting(): string {
    return AppConsts.remoteServiceBaseUrl;
}

export function getStripeApiKeyPublicPage(): string {
    return AppConsts.appStripeApiKey;
}

export function getStripeApiKeySubscribePlans(): string {
    return AppConsts.appStripeApiKey;
}

export function getStripeApiKeySubscribePlansBS(): string {
    return AppConsts.appStripeApiKey;
}

export function getStripeApiKeySubscribePlansIEC(): string {
    return AppConsts.appStripeApiKey;
}

export function getCurrentLanguage(): string {
    return abp.localization.currentLanguage.name;
}

export function getBaseHref(platformLocation: PlatformLocation): string {
    var baseUrl = platformLocation.getBaseHrefFromDOM();
    if (baseUrl) {
        return baseUrl;
    }

    return '/';
}

function handleLogoutRequest(authService: AppAuthService) {
    let currentUrl = UrlHelper.initialUrl;
    let returnUrl = UrlHelper.getReturnUrl();
    if (currentUrl.indexOf(('account/logout')) >= 0 && returnUrl) {
        authService.logout(true, returnUrl);
    }
}

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppModule,
        CommonModule.forRoot(),
        AbpModule,
        ServiceProxyModule,
        HttpClientModule,
        RootRoutingModule,
        NgSelectModule,
        FormsModule,
        NgbModule,
        UtilsModule,
        NgOtpInputModule,
        NgxIntlTelInputModule,
        LoaderComponent,
        SubscriptionPlanComponent,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !window.location.hostname.toLowerCase().includes(environment.WebFacingHost.toLowerCase()),
            registrationStrategy: 'registerWhenStable:30000'
        })
    ],
    exports: [],
    declarations: [
        RootComponent,
        AnonymousUserPageComponent
    ],
    providers: [
        LoginService,
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.RecaptchSiteKey,
        },
        RecaptchaLoaderService,
        ReCaptchaV3Service,
        // { provide: HTTP_INTERCEPTORS, useClass: AbpHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true },
        { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
        { provide: API_BASE_URL2, useFactory: getRemoteServiceCalBaseUrlSetting },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [Injector, PlatformLocation],
            multi: true
        },
        {
            provide: LOCALE_ID,
            useFactory: getCurrentLanguage
        },
        { provide: API_STRIPE_KEY_Public, useFactory: getStripeApiKeyPublicPage },
        { provide: API_STRIPE_KEY_SubscribePlans, useFactory: getStripeApiKeySubscribePlans },
        { provide: TitleStrategy, useClass: FixedStandaloneTitleStrategy },
        { provide: RollbarService, useFactory: rollbarFactory },
        { provide: ErrorHandler, useClass: RollbarErrorHandler },
        {
            provide: TokenService,
            useClass: CustomTokenService
        }
    ],
    bootstrap: [RootComponent]
})
export class RootModule {

}